import React, { useState, useEffect, useMemo, useCallback } from "react";
import { ListGroup, Overlay, Popover } from "react-bootstrap";
import {
  Reply,
  Pencil,
  Copy,
  CheckCircle,
  Trash,
  XCircle,
  Download,
  ArrowCounterclockwise,
} from "react-bootstrap-icons";
import { useMessagesStore } from "@/entities/message";
import { useClipboard } from "use-clipboard-copy";
import { MessageContextMenuProps } from "./message-context-menu.props";
import {
  copyImageToClipboard,
  downloadFile,
} from "@/shared/lib";
import { getCachedBlob } from "@/shared/lib/blob-cache-persistent";
import { useSnackbar } from "notistack";
import { DeleteConfirmAction } from "@/shared/ui";

export const MessageContextMenu = React.memo(
  ({
    show,
    target,
    chatId,
    onHide,
    activeMsg,
    modeSelected,
    onSelectMessage,
    onCopySelected,
    onDeleteSelected,
    onDeleteMessage,
    onCancelSelection,
    isUser,
    setShowSelectChatModal,
    onForwardSelected,
    onResend,
  }: MessageContextMenuProps) => {
    const clipboard = useClipboard();
    const { setReplyingToMessage, setEditingMessage, setForwardMessage } = useMessagesStore();
    const { enqueueSnackbar } = useSnackbar();

    const onCopyText = (text: string) => {
      clipboard.copy(text);
    };

    // === Состояние для кэшированного файла ===
    const [cachedFile, setCachedFile] = useState<{
      blob: Blob;
      url: string;
    } | null>(null);
    const [loadingFile, setLoadingFile] = useState(false);

    useEffect(() => {
      let isCancelled = false;

      const loadFile = async () => {
        if (!activeMsg?.file || typeof activeMsg.file !== "string") {
          setCachedFile(null);
          return;
        }

        setLoadingFile(true);
        try {
          const cached = await getCachedBlob(activeMsg.file);
          if (!isCancelled) {
            setCachedFile(cached);
          }
        } catch (error) {
          console.error("Failed to load file:", error);
          if (!isCancelled) {
            setCachedFile(null);
          }
        } finally {
          if (!isCancelled) {
            setLoadingFile(false);
          }
        }
      };

      loadFile();

      return () => {
        isCancelled = true;
      };
    }, [activeMsg?.file]);

    const fileUrl = cachedFile?.url || null;

    const filename = useMemo(() => {
      if (!activeMsg?.file) return "file";
      if (typeof activeMsg.file === "string") {
        return activeMsg.file.split("/").pop() || "file";
      }
      return activeMsg.file.name;
    }, [activeMsg?.file]);

    const contextMenuItems = useMemo(() => {
      if (modeSelected) {
        return [
          {
            label: "Копировать выбранное как текст",
            icon: Copy,
            action: "copySelected",
          },
          { label: "Переслать выбранное", icon: Reply, action: "forwardSelected" },
          { label: "Удалить выбранное", icon: Trash, action: "deleteSelected" },
          { label: "Отменить выбор", icon: XCircle, action: "cancelSelection" },
        ];
      }

      if (activeMsg?.msg._id.includes("temp-")) {
        const items = [];

        items.push({ label: "Отправить снова", icon: ArrowCounterclockwise, action: "resend" });
        items.push({ label: "Удалить", icon: Trash, action: "delete" });

        if (activeMsg.file) {
          items.push({
            label: "Скачать файл",
            icon: Download,
            action: "saveFile",
          });
        }

        return items;
      }

      const hasFile = !!activeMsg?.file;
      const fileType = activeMsg?.type;

      if (hasFile && fileType) {
        const items = [];

        if (fileType === "image" && fileUrl) {
          items.push({
            label: "Копировать изображение",
            icon: Copy,
            action: "copyImage",
          });
        }

        if (fileUrl) {
          items.push({
            label: "Копировать URL файла",
            icon: Copy,
            action: "copyFileLink",
          });
        }

        items.push({
          label: "Скачать файл",
          icon: Download,
          action: "saveFile",
        });
        items.push({ label: "Ответить", icon: Reply, action: "reply" });
        items.push({ label: "Переслать", icon: Reply, action: "forward" });
        items.push({ label: "Выделить", icon: CheckCircle, action: "select" });

        if (isUser) {
          if (!activeMsg.msg.forwardedFrom) {
            items.push({ label: "Изменить", icon: Pencil, action: "edit" });
          }
          items.push({ label: "Удалить", icon: Trash, action: "delete" });
        }

        return items;
      }

      // Текстовое сообщение
      let items = [
        { label: "Переслать", icon: Reply, action: "forward" },
        { label: "Ответить", icon: Reply, action: "reply" },
        { label: "Копировать", icon: Copy, action: "copy" },
        { label: "Выделить", icon: CheckCircle, action: "select" },
      ];

      if (isUser) {
        if (!activeMsg.msg.forwardedFrom) {
          items.push({ label: "Изменить", icon: Pencil, action: "edit" });
        }
        items.push({ label: "Удалить", icon: Trash, action: "delete" });
      }

      return items;
    }, [modeSelected, activeMsg, isUser, fileUrl]);

    // === Обработка действий ===
    const handleContextAction = useCallback(
      async (action: string) => {
        if (modeSelected) {
          switch (action) {
            case "copySelected":
              onCopySelected();
              break;
            case "deleteSelected":
              onDeleteSelected();
              break;
            case "forwardSelected":
              onForwardSelected();
              break;
            case "cancelSelection":
              onCancelSelection();
              break;
          }
          onHide();
          return;
        }

        if (!activeMsg) return;

        switch (action) {
          case "reply":
            setReplyingToMessage(chatId, activeMsg.msg);
            break;
          case "copy":
            onCopyText(activeMsg.msg.text || "");
            break;
          case "copyFileLink":
            if (fileUrl) {
              clipboard.copy(fileUrl);
            }
            break;
          case "copyImage":
            if (!cachedFile?.blob || !cachedFile?.url) {
              enqueueSnackbar("Изображение недоступно", { variant: "warning" });
              return;
            }

            try {
              await copyImageToClipboard(cachedFile.blob);
              enqueueSnackbar("Изображение скопировано!", {
                variant: "success",
              });
            } catch (err) {
              console.error("Ошибка копирования:", err);

              let message = "Не удалось скопировать изображение";

              if (err instanceof DOMException) {
                if (err.name === "NotAllowedError") {
                  message =
                    "Разрешите доступ к буферу обмена или повторите попытку";
                } else if (err.name === "InvalidStateError") {
                  message = "Неподдерживаемый формат изображения";
                }
              }

              downloadFile(cachedFile.url, filename);
              enqueueSnackbar(`${message}. Файл сохранён на устройство.`, {
                variant: "info",
              });
            }
            break;
          case "saveFile":
            if (activeMsg?.msg._id.includes("temp-") && activeMsg?.file instanceof File) {
              const file = activeMsg.file;
              const url = URL.createObjectURL(file);
              downloadFile(url, file.name || filename);
              URL.revokeObjectURL(url);
            } else if (fileUrl) {
              downloadFile(fileUrl, filename);
            }
            break;
          case "edit":
            setEditingMessage(chatId, activeMsg.msg);
            break;
          case "resend":
            onResend()
            break;
          case "forward":
            setShowSelectChatModal(true);
            break;
          case "select":
            onSelectMessage(activeMsg.msg._id);
            break;
          case "delete":
            onDeleteMessage([activeMsg.msg._id]);
            break;
        }
        onHide();
      },
      [
        modeSelected,
        activeMsg,
        chatId,
        setReplyingToMessage,
        setEditingMessage,
        onCopyText,
        onHide,
        onSelectMessage,
        onCopySelected,
        onDeleteSelected,
        onCancelSelection,
        fileUrl,
        filename,
      ]
    );

    if (!show || !target) return null;

    return (
      <Overlay
        show={show}
        target={target}
        placement="bottom"
        containerPadding={0}
        rootClose
        popperConfig={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 8],
              },
            },
            {
              name: "preventOverflow",
              options: {
                boundary: "viewport",
                padding: 8,
              },
            },
            {
              name: "flip",
              options: {
                fallbackPlacements: ["left-start", "right-start", "top", "bottom"],
              },
            },
          ],
        }}

        onHide={onHide}
        flip={true}
      >
        <Popover style={{ zIndex: 1060, padding: 0 }} id="popover-message">
          <Popover.Body style={{ padding: 0 }}>
            <ListGroup style={{ margin: 0, width: "100%" }}>
              {contextMenuItems.map(({ label, icon: Icon, action }) => {
                const item = (
                  <ListGroup.Item
                    key={action}
                    action
                    variant="light"
                    className="d-flex align-items-center w-100"
                    style={{
                      padding: "6px 14px 6px 12px",
                      fontSize: "0.7rem",
                      border: "none",
                      cursor: "pointer",
                      opacity: loadingFile && action === "saveFile" ? 0.6 : 1,
                      pointerEvents: loadingFile ? "none" : "auto",
                    }}
                  >
                    <Icon
                      className="me-2"
                      style={{
                        transform:
                          action === "forward" || action === "forwardSelected"
                            ? "scaleX(-1)"
                            : "none",
                      }}
                      size={14}
                    />
                    {label}
                  </ListGroup.Item>
                );

                if (action === "delete") {
                  return (
                    <DeleteConfirmAction
                      key={action}
                      triggerElement={item}
                      confirmText="Удалить это сообщение?"
                      onConfirm={() => {
                        onDeleteMessage([activeMsg!.msg._id]);
                        onHide();
                      }}
                      onCloseMenu={onHide}
                    />
                  );
                }

                if (action === "deleteSelected") {
                  return (
                    <DeleteConfirmAction
                      key={action}
                      triggerElement={item}
                      confirmText="Удалить выбранные сообщения?"
                      onConfirm={() => {
                        onDeleteSelected();
                        onHide();
                      }}
                      onCloseMenu={onHide}
                    />
                  );
                }

                return (
                  <ListGroup.Item
                    key={action}
                    action
                    variant="light"
                    onClick={() => handleContextAction(action)}
                    className="d-flex align-items-center w-100"
                    style={{
                      padding: "6px 14px 6px 12px",
                      fontSize: "0.7rem",
                      border: "none",
                      cursor: "pointer",
                      opacity: loadingFile && action === "saveFile" ? 0.6 : 1,
                      pointerEvents: loadingFile ? "none" : "auto",
                    }}
                  >
                    <Icon
                      className="me-2"
                      style={{
                        transform:
                          action === "forward" || action === "forwardSelected"
                            ? "scaleX(-1)"
                            : "none",
                      }}
                      size={14}
                    />
                    {label}
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          </Popover.Body>
        </Popover>
      </Overlay>
    );
  }
);
